import {
  useRef,
  useContext,
  useLayoutEffect,
  useEffect,
  useState,
  useMemo,
  useCallback
} from 'react';
import PropTypes from 'prop-types';
import ParallaxContext from 'components/ParallaxWrapperFiber/context';
import { head } from 'ramda';
import { ParallaxImageStyled, StyledLayerImage, Layer } from './styled';

const getUid = (() => {
  let count = 1;
  return () => count++;
})();

const ParallaxImage = ({
  layers = [],
  parallaxAmount = 100,
  snapMiddle = false,
  bleedColor,
  bleedColorBehind = false,
  ...restProps
}) => {
  const mounted = useRef(true);
  const ref = useRef();
  const idRef = useRef(getUid());
  const { addParallax, removeParallax, scrollRef, isResizing, isEnabled, version } =
    useContext(ParallaxContext);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const firstLayer = head(layers);

  const reversedLayers = useMemo(() => [...layers].reverse(), [layers]);

  const handleResize = useCallback(() => {
    const id = idRef.current;
    const el = ref && ref.current;
    const { width: imageWidth, height: imageHeight } = imageSize;
    if (!el || !id || !imageWidth || !imageHeight) return;

    const { top: elTop, left: blockLeft, width: blockWidth } = el.getBoundingClientRect();
    const blockTop = scrollRef.current + elTop;
    const blockScale = blockWidth / imageWidth;
    const blockHeight = imageHeight * blockScale;
    el.style.height = `${blockHeight}px`;

    addParallax({
      id,
      imageWidth,
      imageHeight,
      blockTop,
      blockLeft,
      blockScale,
      blockWidth,
      blockHeight,
      layers: reversedLayers,
      parallaxAmount,
      snapMiddle,
      bleedColor,
      bleedColorBehind
    });
  }, [
    idRef,
    addParallax,
    scrollRef,
    imageSize,
    reversedLayers,
    parallaxAmount,
    snapMiddle,
    bleedColor,
    bleedColorBehind
  ]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, [mounted]);

  useEffect(() => {
    if (!firstLayer) return;
    const img = new Image();
    img.onload = () => {
      if (mounted.current) {
        setImageSize({ width: img.width, height: img.height });
      }
    };
    img.src = firstLayer;
  }, [firstLayer, mounted]);

  // happens when resize ends
  useLayoutEffect(() => {
    const id = idRef.current;
    if (!id) return;
    let timeout;

    if (!isResizing) {
      handleResize();
      timeout = setTimeout(handleResize, 200);
    }

    return () => {
      clearTimeout(timeout);
      removeParallax({ id });
    };
  }, [idRef, removeParallax, isResizing, handleResize, imageSize]);

  useLayoutEffect(() => {
    handleResize();
  }, [version, handleResize]);

  if (isEnabled) {
    return <ParallaxImageStyled {...restProps} ref={ref} />;
  }
  return (
    <StyledLayerImage {...restProps}>
      {reversedLayers.map((layer, i) => (
        <Layer key={i} src={layer} alt="" />
      ))}
    </StyledLayerImage>
  );
};

ParallaxImage.propTypes = {
  layers: PropTypes.array.isRequired,
  parallaxAmount: PropTypes.number,
  snapMiddle: PropTypes.bool,
  bleedColor: PropTypes.string,
  bleedColorBehind: PropTypes.bool
};

export default ParallaxImage;
